import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Fetches all type-level permission grants for the current user in a single
 * query, replacing 50+ individual `bulkCheck` calls per page load.
 *
 * Returns a synchronous `hasTypePermission(action, objectType)` lookup
 * function and an `isLoading` flag.
 *
 * Only active when the `trpc` feature flag is enabled. When disabled, the
 * query is not fired, `isLoading` remains `true` and `isError` is always
 * `false`.
 *
 * `isError` is consumed by `PermissionsProvider`, which withholds the whole
 * authenticated tree while permissions are unknown. Every consumer fails
 * closed on its own (`hasTypePermission` returns false for everything), so
 * this is about telling the user the truth, not about access control.
 */
export const useTypePermissions = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();

  const { data, isPending, isError, refetch } = useQuery({
    ...trpc.frontend.permission.getTypePermissions.queryOptions(),
    enabled: trpcEnabled,
    gcTime: 30 * 60 * 1000,
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: false,
    // `refetchOnMount: false` only governs a query that already HAS data —
    // query-core gates that branch on `data !== undefined`
    // (`shouldFetchOnMount`). A failed permissions read has none, so without
    // this every remount silently re-fires it: an implicit, unbounded retry on
    // top of the three `shouldRetryTrpcQuery` already spends, once per
    // navigation. PermissionsProvider offers an explicit Retry instead, which
    // is bounded by the user's patience rather than by the router (COI-1062).
    retryOnMount: false,
  });

  const permissionSet = useMemo(() => new Set(data ?? []), [data]);

  const hasTypePermission = useCallback(
    (action: string, objectType: string): boolean =>
      permissionSet.has(`${action}:${objectType}`),
    [permissionSet]
  );

  // `isPending` (no data yet) rather than `isLoading` (`isPending &&
  // isFetching`): an enabled-but-idle query has `isLoading === false` while
  // `data` is still undefined, which would briefly report "loaded, no
  // permissions" and flash denied UI. `isPending` stays true until data lands.
  return {
    hasTypePermission,
    isLoading: trpcEnabled ? isPending : true,
    // `hasTypePermission` cannot distinguish "denied" from "never answered" —
    // both are a false — so the failure has to be reported separately or the
    // app renders a definite Access Denied for a read that errored.
    //
    // Only meaningful under V3: the legacy path resolves permissions from the
    // `role_access` catalog and leaves this query disabled, so it can never be
    // in error there.
    isError: trpcEnabled && isError,
    refetch,
  };
};
